import React, { useRef, useState } from 'react';
import { Alert, Box, LinearProgress, Tab, Tabs, TextField, Typography } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useReleaseNoteImageUpload } from '../useReleaseNoteImageUpload';

interface Props {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  rows?: number;
}

/**
 * Release-note body editor with GitHub-style Edit/Preview tabs.
 *
 * Pasting or dropping an image uploads it and splices the markdown in at the caret.
 * Images go to a publicly-readable container rather than the usual auth-proxied
 * path, because the same markdown is rendered into the email — where a recipient's
 * mail client sends no token and a proxied URL would show a broken image.
 *
 * Raw HTML is not rendered (no rehype-raw), matching the backend, which escapes
 * markup before rendering the email body.
 */
const MarkdownBodyField: React.FC<Props> = ({
  value,
  onChange,
  disabled = false,
  rows = 12,
}) => {
  const [tab, setTab] = useState<'edit' | 'preview'>('edit');
  const [isDragging, setIsDragging] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const { isUploading, error, clearError, handlePaste, handleDrop } =
    useReleaseNoteImageUpload({ value, onChange, textareaRef });

  const onDrop = (e: React.DragEvent<HTMLElement>) => {
    setIsDragging(false);
    handleDrop(e);
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: isDragging ? 'primary.main' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          transition: 'border-color 120ms ease',
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={onDrop}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#FAFBFC',
          }}
        >
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 38,
              '& .MuiTab-root': {
                minHeight: 38,
                textTransform: 'none',
                fontSize: 13,
                py: 0,
              },
            }}
          >
            <Tab value="edit" label="Edit" />
            <Tab value="preview" label="Preview" />
          </Tabs>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', pr: 1.5, display: { xs: 'none', sm: 'block' } }}
          >
            Markdown supported · paste or drop an image
          </Typography>
        </Box>

        {isUploading && <LinearProgress />}

        {tab === 'edit' ? (
          <TextField
            inputRef={textareaRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onPaste={handlePaste}
            disabled={disabled}
            multiline
            rows={rows}
            fullWidth
            placeholder={'## What\'s new\n\n- Added ...\n- Fixed ...'}
            variant="standard"
            InputProps={{ disableUnderline: true, sx: { p: 1.5, fontSize: 14 } }}
          />
        ) : (
          <Box
            sx={{
              p: 2,
              minHeight: rows * 23,
              fontSize: 14,
              '& h1, & h2, & h3': { mt: 1.5, mb: 1 },
              '& ul, & ol': { pl: 3 },
              '& p': { my: 1 },
              '& img': { maxWidth: '100%' },
              '& code': {
                bgcolor: '#f5f5f5',
                px: 0.5,
                borderRadius: 0.5,
                fontSize: 13,
              },
              '& table': { borderCollapse: 'collapse', width: '100%' },
              '& th, & td': { border: '1px solid #e0e0e0', p: 0.75, textAlign: 'left' },
            }}
          >
            {value.trim() ? (
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{value}</ReactMarkdown>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nothing to preview yet.
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={clearError}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default MarkdownBodyField;
